import { Eye, LogOut, Menu, Monitor, Moon, Sun, User, UserCog, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ALL_ROLE_CODES, type RoleCode } from '@tracker/shared';
import { Button } from '@/components/ui/button';
import { profileService } from '@/services/profile.service';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useTheme, type Theme } from '@/contexts/ThemeContext';
import { env } from '@/config/env';

interface HeaderProps {
  onMenuClick: () => void;
}

const THEME_OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

export function Header({ onMenuClick }: HeaderProps) {
  const { user, roles, actualRoles, viewAsRole, setViewAsRole, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const displayName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : (user?.email ?? '');

  const initials = displayName
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const profileQ = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => profileService.getMe(),
  });
  const photoUrl = profileQ.data?.photo_url ?? profileQ.data?.profile?.avatar_url ?? null;

  const showDevTools = env.DEBUG_MODE;
  const isImpersonating = !!viewAsRole;
  const currentRole = roles[0] ?? 'No role';

  return (
    <>
      {/* Dev impersonation banner */}
      {isImpersonating && (
        <div className="flex items-center justify-between gap-3 border-b border-amber-400/40 bg-amber-400/15 px-4 py-1.5 text-xs text-amber-900 dark:text-amber-200 sm:px-6 md:px-8">
          <span className="flex items-center gap-2">
            <Eye className="h-3.5 w-3.5" />
            <strong>Dev preview:</strong> viewing UI as <code className="font-mono">{viewAsRole}</code>. Real role:{' '}
            <code className="font-mono">{actualRoles[0] ?? '—'}</code>. Backend still enforces your real
            role.
          </span>
          <button
            type="button"
            onClick={() => setViewAsRole(null)}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-400/50 bg-amber-400/20 px-2 py-0.5 font-medium transition-colors hover:bg-amber-400/30"
          >
            <X className="h-3 w-3" /> Exit preview
          </button>
        </div>
      )}

      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border/70 bg-background px-4 sm:px-6 md:px-8">
        <button
          type="button"
          onClick={onMenuClick}
          className="rounded-md p-2 text-foreground/60 transition-colors hover:bg-foreground/5 hover:text-foreground md:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="flex-1" />

        <div className="flex items-center gap-1 sm:gap-1.5">
          {/* Dev role switcher */}
          {showDevTools && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 text-foreground/70 hover:text-foreground sm:h-9 sm:w-9"
                  aria-label="Switch viewed role (dev)"
                >
                  <UserCog className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
                  {isImpersonating && (
                    <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-background" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>View as (dev)</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={viewAsRole ?? ''}
                  onValueChange={(v) => setViewAsRole((v || null) as RoleCode | null)}
                >
                  <DropdownMenuRadioItem value="">My real role</DropdownMenuRadioItem>
                  <DropdownMenuSeparator />
                  {ALL_ROLE_CODES.map((r) => (
                    <DropdownMenuRadioItem key={r} value={r}>
                      {r}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Account menu (profile + theme + logout) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="h-9 w-9 overflow-hidden rounded-full ring-offset-background transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:shadow-md sm:h-11 sm:w-11"
                aria-label="Open account menu"
              >
                {photoUrl ? (
                  <img src={photoUrl} alt={displayName} className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-gradient-to-br from-teal-600 to-teal-700 text-[11px] font-bold text-white sm:text-[12px]">
                    {initials || '?'}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-[calc(100dvh-5rem)] w-[min(240px,calc(100vw-2rem))]"
            >
              <div className="px-2 py-1.5">
                <div className="truncate text-sm font-semibold leading-tight">{displayName}</div>
                {user?.email && (
                  <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                )}
                <span className="mt-1.5 inline-flex items-center rounded-full bg-accent px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-accent-foreground">
                  {currentRole}
                </span>
              </div>

              <DropdownMenuSeparator />

              <DropdownMenuItem onSelect={() => navigate('/account/me')}>
                <User />
                My Profile
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuLabel>Theme</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
                {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
                  <DropdownMenuRadioItem key={value} value={value}>
                    <Icon />
                    {label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onSelect={handleLogout}
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
              >
                <LogOut />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    </>
  );
}
